// @flow

import React, { Component } from 'react';
import { Form, Button, Container } from 'reactstrap';
import { withRouter } from 'react-router';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { connect } from 'react-redux';

import StyleClasses from '../../styles/StyleClasses';
import LoadingContainer from '../basicElements/LoadingContainer';
import ControlledInputField from '../basicElements/inputFields/ControlledInputField';
import TypeaheadInputField from '../basicElements/inputFields/TypeaheadInputField';
import { buildUrl, RequestStatus } from '../../store/requests';

import type { StoredEntity } from '../../entities/abstract/StoredEntity';
import type { EntityFieldCollection, FileField } from '../../entities/types';
import { getDefaultValueForField } from '../helpers';
import BooleanControlledInputField from '../basicElements/inputFields/BooleanControlledInputField';
import type { AppState } from '../../store/app/types';
import type { InternalFormData, Suggestions } from '../types';
import { fetchEntity, fetchSuggestions, edit, cleanUpEdit, removeMedia } from '../../store/edit/actions';
import FileInputField from '../basicElements/inputFields/FileInputField';
import ImageInputField from '../basicElements/inputFields/ImageInputField';

//
// PROPS
//

type PropsType = {
    // MODE
    model: $Shape<StoredEntity>,
    modeName: string,
    titleGetter: (entity: StoredEntity) => string,
    // INJECTED FUNCTIONS
    fetchEntity: (model: $Shape<StoredEntity>, modeName: string, id: string) => void,
    fetchSuggestions: (modeName: string, fields: EntityFieldCollection) => void,
    edit: (
        model: $Shape<StoredEntity>,
        modeName: string,
        fields: EntityFieldCollection,
        fileFields: EntityFieldCollection,
        entity: StoredEntity,
        data: InternalFormData,
    ) => void,
    removeMedia: (
        model: $Shape<StoredEntity>,
        modeName: string,
        field: FileField,
        entity: StoredEntity,
        mediaId: string,
    ) => void,
    onSuccess: (history: any) => void,
    cleanUpEdit: (modeName: string) => void,
    // STATE
    entity: StoredEntity,
    suggestions: Suggestions,
    editStatus: RequestStatus,
    // ROUTER
    history: any,
    match: any,
};

//
// CLASS
//

class EditForm extends Component<PropsType, {}> {
    fields: EntityFieldCollection;

    constructor(props) {
        super(props);
        this.fields = {};
        this.fileFields = {};
        this.state = {};

        Object.entries(this.props.model.fields).forEach(([name, field]) => {
            if (field.canEdit) {
                this.fields[name] = field;
                this.state[name] = getDefaultValueForField(field);
            }
        });

        Object.entries(this.props.model.fileFields).forEach(([name, field]) => {
            this.fileFields[name] = field;
            this.state[name] = null;
        });
    }

    changeFormState(id, value) {
        this.setState({
            [id]: value,
        });
    }

    submit() {
        this.props.edit(
            this.props.model,
            this.props.modeName,
            this.fields,
            this.fileFields,
            this.props.entity,
            this.state,
        );
    }

    removeMediaSubmit(field: FileField, mediaId: string) {
        this.props.removeMedia(this.props.model, this.props.modeName, field, this.props.entity, mediaId);
    }

    initForm() {
        const fieldsToFetch = {};

        Object.entries(this.fields).forEach(([name, field]) => {
            if (field.dataType.primitive === 'ref') {
                fieldsToFetch[name] = field;
            }
        });

        const entityId = this.props.match.params.id;
        this.props.fetchEntity(this.props.model, this.props.modeName, entityId);
        this.props.fetchSuggestions(this.props.modeName, fieldsToFetch);
    }

    deinitForm() {
        this.props.cleanUpEdit(this.props.modeName);
    }

    componentDidMount() {
        this.initForm();
    }

    componentDidUpdate(prevProps: PropsType): * {
        const e = this.props.entity;
        const s = this.props.suggestions;
        if (e && s && (!prevProps.entity || !prevProps.suggestions)) {
            // Set the fields to their initial values
            const toSet = {};

            Object.entries(this.props.model.fields).forEach(([name, field]) => {
                if (field.canEdit) {
                    switch (field.dataType.primitive) {
                        case 'ref': {
                            if (field.dataType.isArray) {
                                toSet[name] = s[name].filter((se) => e.record[field.recordName].includes(se.id));
                            } else {
                                toSet[name] = s[name].filter((se) => se.id === e.record[field.recordName]);
                            }
                            break;
                        }
                        case 'simple-entity':
                            toSet[name] = JSON.stringify(e.record[field.recordName]);
                            break;
                        case 'enum':
                            toSet[name] = field.dataType.isArray ? JSON.stringify(e.record[field.recordName]) : e.record[field.recordName];
                            break;
                        case 'amt':
                            toSet[name] = e.record[field.recordName] / 100;
                            break;
                        default:
                            toSet[name] = e.record[field.recordName];
                    }
                }
            });

            this.setState(toSet);
        } else {
            // It's impossible to receive both the entity and the request status at the same time, so this is inside else
            const rqS = this.props.editStatus;
            if (rqS === 'ok') {
                // Refetch the updated entities
                this.deinitForm();
                this.initForm();
            }
        }
    }

    componentWillUnmount(): void {
        this.deinitForm();
    }

    getControlsForFields(): [Component] {
        const fields = [];

        Object.entries(this.fields).forEach(([name, field]) => {
            let control;

            // TODO: Handle plural values
            switch (field.dataType.primitive) {
                case 'str':
                    control = (
                        <ControlledInputField
                            id={`${name}`}
                            key={`${name}`}
                            displayName={`${field.displayName}`}
                            inputType="text"
                            value={this.state[name]}
                            onChange={(id, value) => this.changeFormState(id, value)}
                        />
                    );
                    break;
                case 'simple-entity':
                    control = (
                        <ControlledInputField
                            id={`${name}`}
                            key={`${name}`}
                            displayName={`${field.displayName}`}
                            inputType="textarea"
                            value={this.state[name]}
                            onChange={(id, value) => this.changeFormState(id, value)}
                        />
                    );
                    break;
                case 'amt':
                    control = (
                        <ControlledInputField
                            id={`${name}`}
                            key={`${name}`}
                            displayName={`${field.displayName}`}
                            inputType="number"
                            value={this.state[name]}
                            onChange={(id, value) => this.changeFormState(id, value)}
                        />
                    );
                    break;
                case 'num':
                case 'ts': // TODO: Make it a date picker
                    control = (
                        <ControlledInputField
                            id={`${name}`}
                            key={`${name}`}
                            displayName={`${field.displayName}`}
                            inputType="number"
                            value={this.state[name]}
                            onChange={(id, value) => this.changeFormState(id, value)}
                        />
                    );
                    break;
                case 'enum':
                    control = (
                        <ControlledInputField
                            id={`${name}`}
                            key={`${name}`}
                            displayName={`${field.displayName}`}
                            inputType={field.dataType.isArray ? 'textarea' : 'number'}
                            value={this.state[name]}
                            onChange={(id, value) => this.changeFormState(id, value)}
                        />
                    );
                    break;
                case 'ref':
                    control = (
                        <TypeaheadInputField
                            id={`${name}`}
                            key={`${name}`}
                            displayName={`${field.displayName}`}
                            maxValues={field.dataType.extraData.maxValues}
                            initialValue={this.state[name]}
                            options={this.props.suggestions[name]}
                            onChange={(id, values) => this.changeFormState(id, values)}
                        />
                    );
                    break;
                case 'bool':
                    control = (
                        <BooleanControlledInputField
                            id={`${name}`}
                            key={`${name}`}
                            displayName={`${field.displayName}`}
                            value={this.state[name]}
                            onChange={(id, value) => this.changeFormState(id, value)}
                        />
                    );
                    break;
                default:
                    break;
            }

            fields.push(control);
        });

        return fields;
    }

    getControlsForFileFields(): [Component] {
        const fileFields = [];
        Object.entries(this.fileFields).forEach(([name, field]) => {
            let control;
            switch (field.fileType) {
                case 'img': {
                    let imageIds = field.isArray
                        ? this.props.entity.record[field.fileName]
                        : [this.props.entity.record[field.fileName]];
                    imageIds = imageIds.filter(Boolean);
                    const imageUrls = imageIds.map((id) =>
                        field.isArray
                            ? buildUrl(this.props.model.endpoints.fileDetail(this.props.entity.id, field.urlName, id))
                            : buildUrl(this.props.model.endpoints.file(this.props.entity.id, field.urlName)),
                    );
                    control = (
                        <ImageInputField
                            id={name}
                            key={name}
                            field={field}
                            imageIds={imageIds}
                            imageUrls={imageUrls}
                            onFileUpdated={(id, value) => this.changeFormState(id, value)}
                            onImageRemove={(id) => this.removeMediaSubmit(field, id)}
                        />
                    );
                    break;
                }
                default:
                    control = (
                        <FileInputField
                            id={name}
                            key={name}
                            displayName={field.displayName}
                            onFileUpdated={(id, value) => this.changeFormState(id, value)}
                        />
                    );
            }
            fileFields.push(control);
        });
        return fileFields;
    }

    render() {
        if (
            this.props.entity === undefined ||
            this.props.suggestions === undefined ||
            this.props.editStatus === 'pending'
        ) {
            return <LoadingContainer />;
        }

        return (
            <div>
                <h2 className={StyleClasses.spacedElement}>{this.props.titleGetter(this.props.entity)}</h2>
                <Container fluid>
                    <Form onSubmit={(event) => event.preventDefault()}>
                        {this.getControlsForFields()}
                        {this.getControlsForFileFields()}
                        <Button className={StyleClasses.bottomSpacedElement} onClick={() => this.submit()}>
                            Submit
                        </Button>
                    </Form>
                </Container>
            </div>
        );
    }
}

//
// REDUX & ROUTER
//

function mapStateToProps(state) {
    const appState: AppState = state.app;
    return appState.modes.availableModes[appState.modes.currentModeName];
}

export default withRouter(
    connect(mapStateToProps, {
        fetchEntity,
        fetchSuggestions,
        edit,
        removeMedia,
        cleanUpEdit,
    })(EditForm),
);
